import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  Archive,
  Bot,
  CircleCheck,
  CircleDashed,
  FolderClosed,
  LogOut,
  MessageSquareQuote,
  Plus,
  ShieldCheck,
  ShieldX,
} from "lucide-react";
import { useAppState } from "@/store/useApp";
import { Course } from "@/types/course";
import logo from "@/assets/logo-full.png";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CourseDialog } from "@/features/courses/CourseDialog";
import { useAuth } from "@/hooks/use-auth";
import { UserAvatar } from "@/components/UserAvatar";
import { useNavigate, useLocation } from "react-router";
import { matchesDocumentView } from "@/features/documents/documentViewFilters";

export const AppSidebar = () => {
  const courses = useAppState((state) => state.courses || []);
  const documents = useAppState((state) => state.documents || []);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const todoCount = documents.filter((doc) => matchesDocumentView(doc, "ready")).length;
  const gradedCount = documents.filter((doc) => matchesDocumentView(doc, "done")).length;
  const flaggedCount = documents.filter((doc) => matchesDocumentView(doc, "flagged")).length;
  const archivedCount = documents.filter((doc) => matchesDocumentView(doc, "archived")).length;

  return (
    <>
      <Sidebar>
        <SidebarHeader className="px-4 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Claire" className="w-[70px]" />
          </div>
        </SidebarHeader>
        <SidebarContent className="flex flex-col h-[calc(100vh-5rem)]">
          <div className="flex-1">
            <SidebarGroup>
              <SidebarGroupLabel>Inbox</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className={`w-full justify-between ${location.pathname === "/inbox/ready" ? "bg-secondary" : ""
                        }`}
                      onClick={() => navigate("/inbox/ready")}
                    >
                      <div className="flex items-center gap-2">
                        <CircleDashed className="w-5 h-5" />
                        <span>Todo</span>
                      </div>
                      <span className="text-muted-foreground">{todoCount}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className={`w-full justify-between ${location.pathname === "/inbox/done" ? "bg-secondary" : ""
                        }`}
                      onClick={() => navigate("/inbox/done")}
                    >
                      <div className="flex items-center gap-2">
                        <CircleCheck className="w-5 h-5" />
                        <span>Graded</span>
                      </div>
                      <span className="text-muted-foreground">
                        {gradedCount}
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className={`w-full justify-between ${location.pathname === "/inbox/flagged" ? "bg-secondary" : ""
                        }`}
                      onClick={() => navigate("/inbox/flagged")}
                    >
                      <div className="flex items-center gap-2">
                        <ShieldX className="w-5 h-5" />
                        <span>Flagged</span>
                      </div>
                      <span className="text-muted-foreground">
                        {flaggedCount}
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className={`w-full justify-between ${location.pathname === "/inbox/archived" ? "bg-secondary" : ""
                        }`}
                      onClick={() => navigate("/inbox/archived")}
                    >
                      <div className="flex items-center gap-2">
                        <Archive className="w-5 h-5" />
                        <span>Archived</span>
                      </div>
                      <span className="text-muted-foreground">
                        {archivedCount}
                      </span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <div className="flex items-center justify-between">
                <SidebarGroupLabel>Courses</SidebarGroupLabel>
                <Button
                  aria-label="Add course"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setIsCreateDialogOpen(true)}
                >
                  <Plus className="h-4 w-4" />
                  <span className="sr-only">Add course</span>
                </Button>
              </div>
              <SidebarGroupContent>
                <SidebarMenu data-test-role="courses-menu">
                  {courses.map((course: Course) => (
                    <SidebarMenuItem key={course.id} data-test-role="courses-menu-item">
                      <SidebarMenuButton
                        className={`w-full justify-between ${location.pathname === `/courses/${course.id}` ? "bg-secondary" : ""
                          }`}
                        onClick={() => navigate(`/courses/${course.id}`)}
                        data-selected={location.pathname === `/courses/${course.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <FolderClosed className="w-5 h-5" />
                          <span>{course.reference}</span>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Claire AI</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className={`w-full ${location.pathname === "/insights" ? "bg-secondary" : ""
                        }`}
                      onClick={() => navigate("/insights")}
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquareQuote className="w-5 h-5" />
                        <span>Insights</span>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className={`w-full ${location.pathname.startsWith("/agents") ? "bg-secondary" : ""
                        }`}
                      onClick={() => navigate("/agents")}
                    >
                      <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5" />
                        <span>Agents</span>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className={`w-full ${location.pathname === "/audits" ? "bg-secondary" : ""
                        }`}
                      onClick={() => navigate("/audits")}
                    >
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5" />
                        <span>Audits</span>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>

          <div className="pb-3 flex items-center justify-between gap-2 mr-2">
            <UserAvatar />
            <Button
              className="mb-1"
              onClick={() => logout()}
              variant="ghost"
            >
              <LogOut className="h-5 w-5 text-gray-800" />
            </Button>
          </div>
        </SidebarContent>
      </Sidebar>

      <CourseDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCourseCreated={(courseId) => navigate(`/courses/${courseId}`)}
      />
    </>
  );
};
